



































































































































































































































































import { mapGetters } from 'vuex'
import { successNotification } from '@/lib/calendesk-js-library/tools/notification'
import CColorPicker from '@/lib/calendesk-js-library/components/CColorPicker.vue'
import mixins from 'vue-typed-mixins'
import PlanActions from '@/builder/mixins/PlanActions'
import { pushEvent } from '@/lib/calendesk-js-library/tools/helpers'
import Page from '@/calendesk/models/DTO/Response/Page'
import Section from '@/calendesk/models/DTO/Response/Section'
import { PageType, SectionsSectionType } from '@/calendesk/models/BuilderTypes'

interface HowtoGuideDef {
  id: string;
  title: string;
  steps: Record<number, string | null>;
  warning?: Record<number, string>;
  info?: Record<number, string>;
}

// Steps live in the lang files as one string per platform+mode,
// newline-separated; a null steps entry means the mode is not
// supported on that platform (warning only).
const HOWTO_GUIDES: Array<HowtoGuideDef> = [
  {
    id: 'wordpress',
    title: 'WordPress',
    steps: { 1: 'widget_howto_wordpress_13', 2: 'widget_howto_wordpress_2', 3: 'widget_howto_wordpress_13' },
    warning: { 1: 'widget_howto_wordpress_warning', 2: 'widget_howto_wordpress_warning', 3: 'widget_howto_wordpress_warning' }
  },
  {
    id: 'wix',
    title: 'Wix',
    steps: { 1: 'widget_howto_wix_1', 2: 'widget_howto_wix_2', 3: null },
    warning: { 2: 'widget_howto_wix_warning_2', 3: 'widget_howto_wix_warning_3' }
  },
  {
    id: 'webwave',
    title: 'WebWave',
    steps: { 1: 'widget_howto_webwave_13', 2: 'widget_howto_webwave_2', 3: 'widget_howto_webwave_13' }
  },
  {
    id: 'squarespace',
    title: 'Squarespace',
    steps: { 1: 'widget_howto_squarespace_13', 2: 'widget_howto_squarespace_2', 3: 'widget_howto_squarespace_13' },
    warning: { 1: 'widget_howto_squarespace_warning', 2: 'widget_howto_squarespace_warning', 3: 'widget_howto_squarespace_warning' }
  },
  {
    id: 'shopify',
    title: 'Shopify',
    steps: { 1: 'widget_howto_shopify_13', 2: 'widget_howto_shopify_2', 3: 'widget_howto_shopify_13' },
    warning: { 1: 'widget_howto_shopify_warning', 2: 'widget_howto_shopify_warning', 3: 'widget_howto_shopify_warning' }
  },
  {
    id: 'googlesites',
    title: 'Google Sites',
    steps: { 1: 'widget_howto_googlesites_1', 2: null, 3: null },
    warning: { 2: 'widget_howto_googlesites_warning', 3: 'widget_howto_googlesites_warning' },
    info: { 1: 'widget_howto_googlesites_info' }
  },
  {
    id: 'jimdo',
    title: 'Jimdo',
    steps: { 1: 'widget_howto_jimdo_13', 2: 'widget_howto_jimdo_2', 3: 'widget_howto_jimdo_13' },
    warning: { 1: 'widget_howto_jimdo_warning', 2: 'widget_howto_jimdo_warning', 3: 'widget_howto_jimdo_warning' }
  },
  {
    id: 'other',
    title: '',
    steps: { 1: 'widget_howto_other_13', 2: 'widget_howto_other_2', 3: 'widget_howto_other_13' }
  }
]

const CALENDAR_COMPONENT_IDS: Array<number> = [
  SectionsSectionType.BOOKING_1,
  SectionsSectionType.BOOKING_2,
  SectionsSectionType.BOOKING_3,
  SectionsSectionType.BOOKING_4,
  SectionsSectionType.BOOKING_5,
  SectionsSectionType.BOOKING_6,
  SectionsSectionType.FLEXIBLE_BOOKING_1,
  SectionsSectionType.FLEXIBLE_BOOKING_2,
  SectionsSectionType.FLEXIBLE_BOOKING_3,
  SectionsSectionType.FLEXIBLE_BOOKING_4,
  SectionsSectionType.CALENDAR_V2,
  SectionsSectionType.CALENDAR_V3
]

export default mixins(PlanActions).extend({
  name: 'AddCodeToWebsite',
  components: { CColorPicker },
  data () {
    return {
      currentMode: 1,
      showCode: false,
      buttonText: null,
      textColor: '#ffffff',
      backgroundColor: '#4650c2',
      buttonPosition: 'right',
      selectedPageUuid: null as string | null,
      autoHeight: true,
      frameMaxWidth: 1280,
      frameWidth: 400,
      frameHeight: 700,
      domainChecked: false,
      previewDoc: '',
      previewTimer: null as number | null
    }
  },
  computed: {
    ...mapGetters({
      domain: 'domain/getDefaultDomain',
      appConfiguration: 'setup/getAppConfiguration',
      draftPages: 'builder/getDraftPages'
    }),
    getFullDomain (): string | null {
      if (this.domain && this.domain.domain) {
        return `https://${this.domain.domain}/`
      }

      return null
    },
    domainMissing (): boolean {
      return this.domainChecked && !this.getFullDomain
    },
    buttonPositions (): Array<{ text: string; value: string }> {
      return [
        { text: this.$trans('widget_button_position_right'), value: 'right' },
        { text: this.$trans('widget_button_position_left'), value: 'left' }
      ]
    },
    howtoGuides (): Array<{ id: string; title: string; steps: string[]; warning: string | null; info: string | null; docUrl: string | null }> {
      const mode = this.currentMode
      return HOWTO_GUIDES.map((def) => {
        const stepsKey = def.steps[mode]
        return {
          id: def.id,
          title: def.id === 'other' ? this.$trans('widget_howto_other_title') : def.title,
          steps: stepsKey ? this.$trans(stepsKey).split('\n') : [],
          warning: def.warning && def.warning[mode] ? this.$trans(def.warning[mode]) : null,
          info: def.info && def.info[mode] ? this.$trans(def.info[mode]) : null,
          docUrl: this.howtoDocUrl(def.id)
        }
      }).filter((guide) => guide.steps.length > 0 || guide.warning)
    },
    publicPages (): Array<Page> {
      return (this.draftPages || []).filter((page: Page) =>
        (page.type === PageType.MAIN_PAGE || page.type === PageType.PAGE) && !page.authOnly)
    },
    pagesForSelect (): Array<{ uuid: string; text: string }> {
      // Pages with a calendar first: that is the page the widget is for.
      const withMark = this.publicPages.map((page: Page) => ({
        uuid: page.uuid,
        calendar: this.pageHasCalendar(page),
        name: page.name
      }))

      return withMark
        .sort((a, b) => Number(b.calendar) - Number(a.calendar))
        .map((page) => ({
          uuid: page.uuid,
          text: page.calendar ? `${page.name} (${this.$trans('widget_page_calendar_suffix')})` : page.name
        }))
    },
    selectedPage (): Page | null {
      return this.publicPages.find((page: Page) => page.uuid === this.selectedPageUuid) || null
    },
    selectedPageHasCalendar (): boolean {
      return this.selectedPage ? this.pageHasCalendar(this.selectedPage) : false
    },
    widgetPageUrl (): string {
      const domain = (this.getFullDomain || '').replace(/\/$/, '')
      if (!this.selectedPage || this.selectedPage.type === PageType.MAIN_PAGE || !this.selectedPage.route) {
        return `${domain}/`
      }

      return `${domain}/${this.selectedPage.route}`
    },
    baseScript (): string {
      return '<!-- Calendesk widget begin -->\n<' + 'link href="https://media.calendesk.com/external/widget/v4/widget.css" rel="stylesheet">\n' +
        '<' + 'script src="https://media.calendesk.com/external/widget/v4/widget.js" type="text/javascript"' + '>' + '<' + '/script>'
    },
    getHeadCode (): string {
      if (this.currentMode === 1) {
        if (this.autoHeight) {
          // The booking page reports its size (calendesk-resize); the inline
          // listener keeps the frame exactly as tall as the calendar.
          return '<!-- Calendesk frame begin -->\n' +
            `<style>.calendesk-frame {width: 100%;display: block;border: none;max-width: ${this.frameMaxWidth}px;margin: 0 auto;height: 700px;}</style>\n` +
            '<' + 'script type="text/javascript"' + '>' +
            'window.addEventListener("message", function (e) {' +
            'var d = e.data;' +
            'if (!d || d.type !== "calendesk-resize" || typeof d.height !== "number") return;' +
            'var frames = document.querySelectorAll(".calendesk-frame");' +
            'for (var i = 0; i < frames.length; i++) {' +
            'if (frames[i].contentWindow === e.source) { frames[i].style.height = d.height + "px"; }' +
            '}});' +
            '<' + '/script>\n<!-- Calendesk frame CSS end -->'
        }

        return '<!-- Calendesk frame CSS begin -->\n' +
          `<style>.calendesk-frame {display: block;border: none;margin: 0 auto;width: ${this.frameWidth}px;max-width: 100%;height: ${this.frameHeight}px;}</style>\n` +
          '<!-- Calendesk frame CSS end -->'
      }

      return this.baseScript + '\n<!-- Calendesk widget end -->'
    },
    getCode (): string {
      if (this.currentMode === 1) {
        const src = this.autoHeight ? `${this.widgetPageUrl}?cdWidget=1` : this.widgetPageUrl
        const scrolling = this.autoHeight ? 'no' : 'yes'
        return `<!-- Calendesk frame begin -->\n<iframe class="calendesk-frame" src="${src}" scrolling="${scrolling}" title="${this.appConfiguration.companyName}" frameBorder="0"></iframe>\n<!-- Calendesk frame end -->`
      } else if (this.currentMode === 2) {
        const position = this.buttonPosition === 'left' ? ", position: 'left'" : ''
        return this.baseScript +
          '<' + 'script type="text/javascript"' + '>' +
          `Calendesk.initBadgeWidget({ url: '${this.widgetPageUrl}', text: '${this.buttonText}', color: '${this.backgroundColor}', textColor: '${this.textColor}', frameless: true${position} });` + '<' + '/script>\n<!-- Calendesk widget end -->'
      }

      return `<!-- Calendesk widget begin -->\n<a href="#" onclick="Calendesk.openModal({ url: '${this.widgetPageUrl}', frameless: true });return false;">${this.buttonText}</a>\n<!-- Calendesk widget end -->`
    },
    previewSource (): string {
      if (!this.showCode || !this.getFullDomain) {
        return ''
      }

      const headCode = this.currentMode === 2 ? '' : this.getHeadCode
      const widgetHtml = this.currentMode === 2 ? '' : this.getCode
      const tailCode = this.currentMode === 2 ? this.getCode : ''

      return '<!doctype html><html><head><meta charset="utf-8">' +
        '<meta name="viewport" content="width=device-width, initial-scale=1">' +
        '<style>' +
        'body{margin:0;font-family:sans-serif;background:#fff;}' +
        'a{color:#4650c2;}' +
        '.pv-nav{display:flex;align-items:center;gap:24px;padding:18px 32px;border-bottom:1px solid #eee;}' +
        '.pv-logo{width:110px;height:16px;border-radius:8px;background:#d7d9e0;}' +
        '.pv-link{width:56px;height:10px;border-radius:5px;background:#e4e6ec;}' +
        '.pv-wrap{max-width:960px;margin:0 auto;padding:32px 24px;}' +
        '.pv-h{width:60%;height:22px;border-radius:8px;background:#d7d9e0;margin:0 0 14px;}' +
        '.pv-l{height:10px;border-radius:5px;background:#eceef2;margin:0 0 10px;}' +
        '.pv-w95{width:95%;}.pv-w80{width:80%;}.pv-w60{width:60%;}' +
        '.pv-widget{margin:28px 0;}' +
        '</style>' +
        headCode +
        '</head><body>' +
        '<div class="pv-nav"><div class="pv-logo"></div><div class="pv-link"></div><div class="pv-link"></div><div class="pv-link"></div></div>' +
        '<div class="pv-wrap">' +
        '<div class="pv-h"></div>' +
        '<div class="pv-l pv-w95"></div><div class="pv-l pv-w80"></div><div class="pv-l pv-w60"></div>' +
        `<div class="pv-widget">${widgetHtml}</div>` +
        '<div class="pv-l pv-w95"></div><div class="pv-l pv-w80"></div>' +
        '</div>' +
        tailCode +
        '</body></html>'
    }
  },
  watch: {
    showCode: {
      immediate: true,
      handler (value: boolean) {
        this.$emit('updateCtaButtonTitle', value ? null : this.$trans('next'))
        this.$emit('updateSecondaryButtonTitle', value ? this.$trans('back') : null)
      }
    },
    domainMissing: {
      immediate: true,
      handler (value: boolean) {
        this.$emit('ctaButtonDisabled', value)
      }
    },
    previewSource: {
      immediate: true,
      handler (value: string) {
        if (this.previewTimer) {
          clearTimeout(this.previewTimer)
        }
        this.previewTimer = window.setTimeout(() => {
          this.previewDoc = value
        }, 400)
      }
    }
  },
  created () {
    pushEvent('wbAddCodeToWebsite')
    if (this.domain) {
      this.domainChecked = true
    } else {
      this.$store.dispatch('domain/fetch').finally(() => {
        this.domainChecked = true
      })
    }
    this.buttonText = this.$trans('widget_dummy_1')
    const firstCalendarPage = this.publicPages.find((page: Page) => this.pageHasCalendar(page))
    this.selectedPageUuid = (firstCalendarPage || this.publicPages[0] || { uuid: null }).uuid
  },
  beforeDestroy () {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer)
    }
  },
  methods: {
    howtoDocUrl (id: string): string | null {
      const locale = (this.$vuetify.lang.current || 'en').substring(0, 2)
      switch (id) {
        case 'wordpress':
          return 'https://wordpress.org/documentation/article/custom-html/'
        case 'wix':
          return this.currentMode === 2
            ? 'https://support.wix.com/en/article/wix-editor-embedding-custom-code-on-your-site'
            : 'https://support.wix.com/en/article/wix-editor-embedding-a-site-or-a-widget'
        case 'squarespace':
          return this.currentMode === 2
            ? 'https://support.squarespace.com/hc/en-us/articles/205815908-Using-code-injection'
            : 'https://support.squarespace.com/hc/en-us/articles/206543167'
        case 'webwave':
          return locale === 'pl'
            ? 'https://pomoc.webwavecms.com/dodawanie-wlasnego-kodu-na-strone'
            : 'https://help.webwave.me/add-a-customized-code-to-your-website'
        case 'shopify':
          return `https://help.shopify.com/${locale}/manual/online-store/themes/customizing-themes/edit-code/edit-theme-code`
        case 'googlesites':
          return `https://support.google.com/sites/answer/90569?hl=${locale}`
        case 'jimdo':
          return locale === 'de'
            ? 'https://help.jimdo.com/hc/de/articles/115005505763'
            : 'https://help.jimdo.com/hc/en-us/articles/115005505763-How-do-I-insert-my-own-code'
        default:
          return null
      }
    },
    pageHasCalendar (page: Page): boolean {
      return (page.sections || []).some((section: Section) =>
        CALENDAR_COMPONENT_IDS.includes(Number(section.configuration?.component_id)))
    },
    goBack () {
      this.showCode = false
    },
    nextStep () {
      if (this.domainMissing) {
        return
      }

      if (this.widgetsAvailable) {
        this.showCode = true
      } else {
        this.openFeatureNotAvailableDialog()
      }
    },
    copied () {
      this.$helpers.copyToClipboard(this.getCode)
      successNotification('copied_to_clipboard')
    },
    copiedHead () {
      this.$helpers.copyToClipboard(this.getHeadCode)
      successNotification('copied_to_clipboard')
    }
  }
})

